import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  Briefcase, MapPin, Clock, DollarSign, Search, Filter, Building2,
  Users, TrendingUp, Plus, Send, ExternalLink, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPES = [
  { value: "full-time", label: "Full Time" },
  { value: "part-time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
  { value: "remote", label: "Remote" }
];

const EXP_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior" },
  { value: "lead", label: "Lead" },
  { value: "executive", label: "Executive" }
];

const VERIFIED_COMPANIES = [
  "SolarGrid MENA", "CarbonTrace Analytics", "EcoPackage Solutions", "FinTech Arabia",
  "DataSense AI", "CloudNine Tech", "GrowthX Ventures", "AppWorks Studio", "CryptoGulf Labs"
];

const JobsPage = () => {
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ job_type: "", experience_level: "", location: "" });
  
  const [postJobOpen, setPostJobOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewJobOpen, setViewJobOpen] = useState(false);
  const [myApplications, setMyApplications] = useState([]);
  const [showMyApps, setShowMyApps] = useState(false);
  
  const [jobForm, setJobForm] = useState({
    title: "", company_name: "", company_email: "", location: "", job_type: "full-time",
    salary_min: "", salary_max: "", currency: "AED", description: "", requirements: "",
    skills: "", experience_level: "mid", industry: ""
  });
  
  const [applyForm, setApplyForm] = useState({
    applicant_name: "", applicant_email: "", phone: "", cover_letter: "", 
    linkedin_url: "", portfolio_url: "", cv_url: "", cover_letter_url: "", business_card_url: ""
  });
  const [uploading, setUploading] = useState({ cv: false, cover_letter: false, business_card: false });
  const [jobsJsonLd, setJobsJsonLd] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchStats();
    if (isAuthenticated) fetchMyApplications();
  }, [isAuthenticated]);

  // Inject Google Jobs JSON-LD for SEO
  useEffect(() => {
    if (jobsJsonLd.length > 0) {
      const existingScript = document.getElementById('google-jobs-jsonld');
      if (existingScript) existingScript.remove();
      
      const script = document.createElement('script');
      script.id = 'google-jobs-jsonld';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jobsJsonLd);
      document.head.appendChild(script);
    }
    return () => {
      const script = document.getElementById('google-jobs-jsonld');
      if (script) script.remove();
    };
  }, [jobsJsonLd]);

  const fetchMyApplications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/jobs/my-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyApplications(response.data || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (filters.job_type && filters.job_type !== "all") params.append("job_type", filters.job_type);
      if (filters.experience_level && filters.experience_level !== "all") params.append("experience_level", filters.experience_level);
      if (filters.location) params.append("location", filters.location);
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data.jobs || []);
      setJobsJsonLd(response.data.jsonld || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/jobs/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const handlePostJob = async () => {
    if (!jobForm.title || !jobForm.company_name || !jobForm.company_email || !jobForm.description) {
      toast({ title: "Error", description: "Please fill all required fields (Title, Company, HR Email, Description)", variant: "destructive" });
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/jobs`, {
        ...jobForm,
        salary_min: jobForm.salary_min ? parseInt(jobForm.salary_min) : null,
        salary_max: jobForm.salary_max ? parseInt(jobForm.salary_max) : null,
        skills: jobForm.skills.split(",").map(s => s.trim()).filter(s => s)
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast({ title: "Success", description: "Job posted! Applications will be sent to " + jobForm.company_email });
      setPostJobOpen(false);
      setJobForm({ title: "", company_name: "", company_email: "", location: "", job_type: "full-time", salary_min: "", salary_max: "", currency: "AED", description: "", requirements: "", skills: "", experience_level: "mid", industry: "" });
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to post job", variant: "destructive" });
    }
  };

  const handleApply = async () => {
    if (!applyForm.applicant_name || !applyForm.applicant_email) {
      toast({ title: "Error", description: "Name and email are required", variant: "destructive" });
      return;
    }
    
    try {
      await axios.post(`${API}/jobs/${selectedJob.id}/apply`, applyForm);
      toast({ title: "Success", description: "Application submitted! Check your email for confirmation." });
      setApplyOpen(false);
      setApplyForm({ applicant_name: "", applicant_email: "", phone: "", cover_letter: "", linkedin_url: "", portfolio_url: "", cv_url: "", cover_letter_url: "", business_card_url: "" });
      fetchMyApplications();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to apply", variant: "destructive" });
    }
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [fileType]: true }));
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/${fileType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const urlField = fileType === 'cv' ? 'cv_url' : fileType === 'cover_letter' ? 'cover_letter_url' : 'business_card_url';
      setApplyForm(prev => ({ ...prev, [urlField]: response.data.url }));
      toast({ title: "Uploaded", description: `${file.name} uploaded successfully` });
    } catch (error) {
      toast({ title: "Upload Failed", description: error.response?.data?.detail || "Failed to upload file", variant: "destructive" });
    }
    
    setUploading(prev => ({ ...prev, [fileType]: false }));
  };

  const formatSalary = (min, max, currency = "AED") => {
    if (!min && !max) return "Competitive";
    const fmt = (n) => n >= 1000 ? `${(n/1000).toFixed(0)}K` : n;
    if (min && max) return `${currency} ${fmt(min)} - ${fmt(max)}`;
    if (min) return `${currency} ${fmt(min)}+`;
    return `Up to ${currency} ${fmt(max)}`;
  };

  const isVerified = (companyName) => VERIFIED_COMPANIES.includes(companyName);

  const getTypeColor = (type) => {
    const colors = {
      "full-time": "bg-green-600", "part-time": "bg-blue-600",
      "contract": "bg-orange-600", "internship": "bg-purple-600", "remote": "bg-cyan-600"
    };
    return colors[type] || "bg-gray-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F0', fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      {/* Hero */}
      <div style={{ position: 'relative', padding: '80px 32px 56px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px)', backgroundSize: '80px 80px' }} />
        <div style={{ position: 'absolute', top: '-30%', right: '-15%', width: '70%', height: '140%', background: 'radial-gradient(ellipse at 70% 40%,rgba(200,168,75,.08) 0%,transparent 60%)' }} />
        <div className="container mx-auto px-6" style={{ position: 'relative', zIndex: 5 }}>
          <div className="max-w-3xl mx-auto text-center">
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.3em', textTransform: 'uppercase', color: '#C8A84B', marginBottom: 20 }}>— Job Board —</div>
            <h1 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(40px,5.6vw,72px)', fontWeight: 700, color: '#0D1B2A', marginBottom: 16, letterSpacing: '-.02em', lineHeight: 1 }}>
              Find your next <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>opportunity.</em>
            </h1>
            <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 20, fontWeight: 300, fontStyle: 'italic', color: '#3A4A5C', marginBottom: 32 }}>Jobs at SKI AXIS portfolio companies and partners.</p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mx-auto">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#7A8190' }} />
                <Input
                  placeholder="Search jobs, companies, skills…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14"
                  style={{ background: '#FFFFFF', color: '#0D1B2A', border: '1px solid rgba(200,168,75,0.35)', borderRadius: 50, fontSize: 14 }}
                />
              </div>
              <Button type="submit" style={{ height: 56, padding: '0 32px', background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.18em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>
                Search
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
            <CardContent className="p-4 text-center">
              <Briefcase className="h-6 w-6 mx-auto mb-2" style={{ color: '#C8A84B' }} />
              <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 38, fontWeight: 700, color: '#0D1B2A', lineHeight: 1 }}>{stats.total_jobs || 0}</p>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7A8190', marginTop: 6 }}>Open Positions</p>
            </CardContent>
          </Card>
          <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
            <CardContent className="p-4 text-center">
              <Building2 className="h-6 w-6 mx-auto mb-2" style={{ color: '#C8A84B' }} />
              <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 38, fontWeight: 700, color: '#0D1B2A', lineHeight: 1 }}>{stats.total_companies || 0}</p>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7A8190', marginTop: 6 }}>Companies Hiring</p>
            </CardContent>
          </Card>
          <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-2" style={{ color: '#C8A84B' }} />
              <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 38, fontWeight: 700, color: '#0D1B2A', lineHeight: 1 }}>{stats.total_applications || 0}</p>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7A8190', marginTop: 6 }}>Applications</p>
            </CardContent>
          </Card>
          <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-6 w-6 mx-auto mb-2" style={{ color: '#C8A84B' }} />
              <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 38, fontWeight: 700, color: '#0D1B2A', lineHeight: 1 }}>{stats.by_type?.remote || 0}</p>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7A8190', marginTop: 6 }}>Remote Jobs</p>
            </CardContent>
          </Card>
        </div>

        {/* My Applications Button */}
        {isAuthenticated && (
          <div className="flex justify-end mb-4">
            <Button 
              onClick={() => setShowMyApps(!showMyApps)} 
              variant={showMyApps ? "default" : "outline"}
              className={showMyApps ? "" : "border-gray-600 text-gray-300"}
              style={showMyApps ? { background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' } : { borderColor: 'rgba(200,168,75,0.2)', color: '#3A4A5C', background: 'transparent', fontWeight: 600, letterSpacing: '.1em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50 }}
            >
              <Briefcase className="h-4 w-4 mr-2" />
              My Applications ({myApplications.length})
            </Button>
          </div>
        )}

        {/* My Applications Section */}
        {showMyApps && isAuthenticated && (
          <Card className="bg-gray-800/50 border-gray-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white">My Job Applications</CardTitle>
            </CardHeader>
            <CardContent>
              {myApplications.length === 0 ? (
                <p className="text-gray-400 text-center py-4">You haven't applied to any jobs yet</p>
              ) : (
                <div className="space-y-3">
                  {myApplications.map((app) => (
                    <div key={app.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                      <div>
                        <h4 className="text-white font-medium">{app.job_title || 'Job'}</h4>
                        <p className="text-gray-400 text-sm">{app.company_name} • {app.job_location}</p>
                        <p className="text-gray-500 text-xs mt-1">Applied {new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={
                        app.status === 'shortlisted' ? 'bg-green-600' :
                        app.status === 'reviewed' ? 'bg-blue-600' :
                        app.status === 'rejected' ? 'bg-red-600' :
                        app.status === 'hired' ? 'bg-purple-600' : 'bg-gray-600'
                      }>
                        {app.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className="lg:w-64 space-y-4">
            <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Filter className="h-4 w-4" /> Filters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Job Type</label>
                  <Select value={filters.job_type} onValueChange={(v) => setFilters({...filters, job_type: v})}>
                    <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Experience</label>
                  <Select value={filters.experience_level} onValueChange={(v) => setFilters({...filters, experience_level: v})}>
                    <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue placeholder="All levels" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All levels</SelectItem>
                      {EXP_LEVELS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Location</label>
                  <Input
                    placeholder="e.g. Dubai, Remote"
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                    className="bg-gray-900 border-gray-600"
                  />
                </div>
                <Button onClick={fetchJobs} className="w-full" style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>Apply Filters</Button>
              </CardContent>
            </Card>

            {/* Post Job CTA */}
            {isAuthenticated && ['admin', 'startup', 'founder'].includes(user?.role) && (
              <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-700">
                <CardContent className="p-4 text-center">
                  <h3 className="text-white font-semibold mb-2">Hiring?</h3>
                  <p className="text-gray-300 text-sm mb-3">Post a job to reach top talent</p>
                  <Button onClick={() => setPostJobOpen(true)} className="w-full bg-white text-purple-700 hover:bg-gray-100">
                    <Plus className="h-4 w-4 mr-2" /> Post a Job
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Job Listings */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">{jobs.length} Jobs Found</h2>
            </div>

            {jobs.length === 0 ? (
              <Card className="border-[rgba(200,168,75,0.15)]" style={{ background: '#FFFFFF', borderRadius: 0 }}>
                <CardContent className="p-12 text-center">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                  <h3 className="text-white text-lg mb-2">No jobs found</h3>
                  <p className="text-gray-400">Try adjusting your filters or check back later</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <Card key={job.id} className="bg-gray-800/50 border-gray-700 hover:border-gray-500 transition-all cursor-pointer" onClick={() => { setSelectedJob(job); setViewJobOpen(true); }}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getTypeColor(job.job_type)}>{job.job_type}</Badge>
                            <Badge variant="outline" className="text-gray-400 border-gray-600">{job.experience_level}</Badge>
                          </div>
                          <h3 className="text-lg font-semibold text-white mb-1">{job.title}</h3>
                          <p className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                            {job.company_name}
                            {isVerified(job.company_name) && (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                                Verified
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{job.location}</span>
                            <span className="flex items-center gap-1"><DollarSign className="h-4 w-4" />{formatSalary(job.salary_min, job.salary_max, job.currency)}</span>
                            <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{new Date(job.created_at).toLocaleDateString()}</span>
                          </div>
                          {job.skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {job.skills.slice(0, 5).map((skill, i) => (
                                <span key={i} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{skill}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button onClick={(e) => { e.stopPropagation(); setSelectedJob(job); setApplyOpen(true); }} className="bg-purple-600 hover:bg-purple-700" style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>
                          Apply <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Post Job Dialog */}
      <Dialog open={postJobOpen} onOpenChange={setPostJobOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post a New Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Job Title *</label>
                <Input value={jobForm.title} onChange={(e) => setJobForm({...jobForm, title: e.target.value})} placeholder="e.g. Senior Developer" className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Company Name *</label>
                <Input value={jobForm.company_name} onChange={(e) => setJobForm({...jobForm, company_name: e.target.value})} placeholder="Your company" className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">HR/Recruiter Email * <span className="text-purple-400">(Applications will be sent here)</span></label>
              <Input type="email" value={jobForm.company_email} onChange={(e) => setJobForm({...jobForm, company_email: e.target.value})} placeholder="hr@company.com" className="bg-gray-900 border-gray-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Location</label>
                <Input value={jobForm.location} onChange={(e) => setJobForm({...jobForm, location: e.target.value})} placeholder="e.g. Dubai, UAE" className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Job Type</label>
                <Select value={jobForm.job_type} onValueChange={(v) => setJobForm({...jobForm, job_type: v})}>
                  <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Min Salary (AED)</label>
                <Input type="number" value={jobForm.salary_min} onChange={(e) => setJobForm({...jobForm, salary_min: e.target.value})} placeholder="15000" className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Max Salary (AED)</label>
                <Input type="number" value={jobForm.salary_max} onChange={(e) => setJobForm({...jobForm, salary_max: e.target.value})} placeholder="25000" className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Experience</label>
                <Select value={jobForm.experience_level} onValueChange={(v) => setJobForm({...jobForm, experience_level: v})}>
                  <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXP_LEVELS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Skills (comma separated)</label>
              <Input value={jobForm.skills} onChange={(e) => setJobForm({...jobForm, skills: e.target.value})} placeholder="React, Node.js, MongoDB" className="bg-gray-900 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Job Description *</label>
              <Textarea value={jobForm.description} onChange={(e) => setJobForm({...jobForm, description: e.target.value})} placeholder="Describe the role..." rows={4} className="bg-gray-900 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Requirements</label>
              <Textarea value={jobForm.requirements} onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})} placeholder="List requirements..." rows={3} className="bg-gray-900 border-gray-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostJobOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handlePostJob} className="bg-purple-600 hover:bg-purple-700" style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>Post Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Apply for {selectedJob?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Full Name *</label>
                <Input value={applyForm.applicant_name} onChange={(e) => setApplyForm({...applyForm, applicant_name: e.target.value})} placeholder="Your name" className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                <Input type="email" value={applyForm.applicant_email} onChange={(e) => setApplyForm({...applyForm, applicant_email: e.target.value})} placeholder="you@email.com" className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Phone</label>
              <Input value={applyForm.phone} onChange={(e) => setApplyForm({...applyForm, phone: e.target.value})} placeholder="+971 50 xxx xxxx" className="bg-gray-900 border-gray-600" />
            </div>
            
            {/* File Uploads */}
            <div className="space-y-3 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
              <h4 className="text-white font-medium mb-2">📎 Attachments</h4>
              
              {/* CV Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">CV / Resume (PDF, DOC, DOCX)</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload(e, 'cv')}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload" className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${applyForm.cv_url ? 'border-green-500 bg-green-900/20' : 'border-gray-600 hover:border-purple-500'}`}>
                      {uploading.cv ? (
                        <span className="text-gray-400">Uploading...</span>
                      ) : applyForm.cv_url ? (
                        <span className="text-green-400">✓ CV Uploaded</span>
                      ) : (
                        <span className="text-gray-400">Click to upload CV</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Cover Letter Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Cover Letter (PDF, DOC, DOCX, TXT)</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => handleFileUpload(e, 'cover_letter')}
                    className="hidden"
                    id="cl-upload"
                  />
                  <label htmlFor="cl-upload" className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${applyForm.cover_letter_url ? 'border-green-500 bg-green-900/20' : 'border-gray-600 hover:border-purple-500'}`}>
                      {uploading.cover_letter ? (
                        <span className="text-gray-400">Uploading...</span>
                      ) : applyForm.cover_letter_url ? (
                        <span className="text-green-400">✓ Cover Letter Uploaded</span>
                      ) : (
                        <span className="text-gray-400">Click to upload Cover Letter</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Business Card Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Business Card (PDF, PNG, JPG)</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="file" 
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => handleFileUpload(e, 'business_card')}
                    className="hidden"
                    id="bc-upload"
                  />
                  <label htmlFor="bc-upload" className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${applyForm.business_card_url ? 'border-green-500 bg-green-900/20' : 'border-gray-600 hover:border-purple-500'}`}>
                      {uploading.business_card ? (
                        <span className="text-gray-400">Uploading...</span>
                      ) : applyForm.business_card_url ? (
                        <span className="text-green-400">✓ Business Card Uploaded</span>
                      ) : (
                        <span className="text-gray-400">Click to upload Business Card</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Links */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">LinkedIn URL</label>
                <Input value={applyForm.linkedin_url} onChange={(e) => setApplyForm({...applyForm, linkedin_url: e.target.value})} placeholder="linkedin.com/in/..." className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Portfolio URL</label>
                <Input value={applyForm.portfolio_url} onChange={(e) => setApplyForm({...applyForm, portfolio_url: e.target.value})} placeholder="yourportfolio.com" className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Cover Letter (Text)</label>
              <Textarea value={applyForm.cover_letter} onChange={(e) => setApplyForm({...applyForm, cover_letter: e.target.value})} placeholder="Why are you interested in this role..." rows={4} className="bg-gray-900 border-gray-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handleApply} className="bg-purple-600 hover:bg-purple-700" style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>
              <Send className="h-4 w-4 mr-2" /> Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Job Dialog */}
      <Dialog open={viewJobOpen} onOpenChange={setViewJobOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title}</DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="py-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge className={getTypeColor(selectedJob.job_type)}>{selectedJob.job_type}</Badge>
                <Badge variant="outline" className="text-gray-400 border-gray-600">{selectedJob.experience_level}</Badge>
              </div>
              <p className="text-purple-400 font-medium text-lg mb-2">{selectedJob.company_name}</p>
              <div className="flex flex-wrap gap-4 text-gray-400 mb-6">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{selectedJob.location}</span>
                <span className="flex items-center gap-1"><DollarSign className="h-4 w-4" />{formatSalary(selectedJob.salary_min, selectedJob.salary_max)}</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-white font-semibold mb-2">Description</h4>
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedJob.description}</p>
                </div>
                {selectedJob.requirements && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Requirements</h4>
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedJob.requirements}</p>
                  </div>
                )}
                {selectedJob.skills?.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.skills.map((skill, i) => (
                        <span key={i} className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewJobOpen(false)} className="border-gray-600">Close</Button>
            <Button onClick={() => { setViewJobOpen(false); setApplyOpen(true); }} className="bg-purple-600 hover:bg-purple-700" style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none' }}>
              <Send className="h-4 w-4 mr-2" /> Apply Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default JobsPage;
